import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from "react-redux";
import { Link, useSearchParams } from 'react-router-dom'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import { Actions as AuthActions } from '../../redux/auth/reducers'
import apiService from '../../services/api/apiService'
import general from '../../utils/general'
import Input from '../../components/input'
import FileUpload from '../../components/file-upload'
import { ValidationPatterns, ValidationMessages } from '../../utils/validation'
import { UserTypeEnum } from '../../utils/enums'
import StudentForm from '../students/StudentForm'
import './index.scss'

const Profile = () => {
  const dispatch = useDispatch()
  const userInfo = useSelector((state) => state.auth?.userInfo || {});
  console.log(userInfo)
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [profileData, setProfileData] = useState({
    id: '',
    email: '',
    firstName: '',
    lastName: '',
    userType: '',
    status: '',
    phone: '',
    avatarUrl: '',
    emailVerified: false
  })
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    avatarUrl: ''
  })
  const [validationErrors, setValidationErrors] = useState({})
  const [activeTab, setActiveTab] = useState('about')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [contractStatus, setContractStatus] = useState(null)
  const [loadingContractStatus, setLoadingContractStatus] = useState(false)
  const [contractError, setContractError] = useState('')

  // Şifre değiştirme state'leri
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [passwordErrors, setPasswordErrors] = useState({})
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [invitationLink, setInvitationLink] = useState('')

  useEffect(() => {
    fetchProfile()
  }, [])

  // URL'den tab parametresini oku ve aktif tab'ı ayarla
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam && ['about', 'student', 'account', 'password', 'contract'].includes(tabParam)) {
      setActiveTab(tabParam)
    }
  }, [searchParams])

  useEffect(() => {
    if (profileData.userType !== 'agent') {
      setInvitationLink('')
      setContractStatus(null)
      setContractError('')
      return
    }

    const agentId = profileData.agentId || profileData.id
    if (!agentId) {
      setInvitationLink('')
      setContractStatus(null)
      setContractError('')
      return
    }

    const linkUrl = new URL('/auth/register', window.location.origin)
    linkUrl.searchParams.set('usertype', 'student')
    linkUrl.searchParams.set('agent', agentId)
    setInvitationLink(linkUrl.toString())
  }, [profileData])

  useEffect(() => {
    if (profileData.userType !== 'agent') {
      return
    }

    const agentId = profileData.agentId || profileData.id
    if (!agentId) {
      return
    }

    setLoadingContractStatus(true)
    setContractError('')
    dispatch(
      ApiCallActions.Get({
        url: 'agents/me/contract-status',
        showAlertOnError: false,
        showLoading: false,
        onSuccess: ({ data }) => {
          const payload = data?.data || data || null
          setContractStatus(payload)
          setLoadingContractStatus(false)
        },
        onError: () => {
          setContractStatus(null)
          setContractError('Sözleşme bilgileri alınamadı')
          setLoadingContractStatus(false)
        }
      })
    )
  }, [dispatch, profileData.userType, profileData.agentId, profileData.id])

  const fetchProfile = async () => {
    try {
      setLoading(true)
      const response = await apiService.makeApiCall({
        url: 'auth/profile',
        method: 'GET'
      })

      if (response.success && response.data) {
        const data = response.data.data || response.data
        const profileDataUpdate = {
          id: data.id || '',
          email: data.email || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          userType: data.userType || '',
          status: data.status || '',
          phone: data.phone || '',
          avatarUrl: data.avatarUrl || '',
          emailVerified: data.emailVerified || false
        }
        
        setProfileData(profileDataUpdate)
        setFormData({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          phone: data.phone || '',
          avatarUrl: data.avatarUrl || ''
        })
        
        // Store'daki userInfo'yu güncelle
        dispatch(AuthActions.setAuthState({
          userInfo: {
            ...userInfo,
            ...data,
            avatarUrl: data.avatarUrl || userInfo.avatarUrl
          }
        }))
      } else {
        general.notificationError(response.errorMessage || 'Profil bilgileri yüklenemedi')
      }
    } catch (error) {
      console.error('Profile fetch error:', error)
      general.notificationError('Profil bilgileri yüklenemedi')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleValidationChange = (fieldName, isValid, errorMessage) => {
    setValidationErrors(prev => ({
      ...prev,
      [fieldName]: isValid ? '' : errorMessage
    }))
  }

  const isFormValid = () => {
    return Object.values(validationErrors).every(error => error === '')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Form validation kontrolü
    if (!isFormValid()) {
      return
    }
    
    setIsSubmitting(true)

    dispatch(
      ApiCallActions.Put({
        url: 'auth/profile',
        data: formData,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: async ({ data }) => {
          general.messageSuccess('Profil başarıyla güncellendi')
          setIsEditing(false)
          setValidationErrors({})
          
          // Profil bilgilerini yeniden çek ve store'u güncelle
          try {
            const response = await apiService.makeApiCall({
              url: 'auth/profile',
              method: 'GET'
            })
            
            if (response.success && response.data) {
              const updatedData = response.data.data || response.data
              setProfileData({
                id: updatedData.id || '',
                email: updatedData.email || '',
                firstName: updatedData.firstName || '',
                lastName: updatedData.lastName || '',
                userType: updatedData.userType || '',
                status: updatedData.status || '',
                phone: updatedData.phone || '',
                avatarUrl: updatedData.avatarUrl || '',
                emailVerified: updatedData.emailVerified || false
              })
              
              // Store'u güncelle
              dispatch(AuthActions.setAuthState({
                userInfo: updatedData
              }))
            }
          } catch (error) {
            console.error('Profile fetch error after update:', error)
          }
        },
        callback: () => setIsSubmitting(false)
      })
    )
  }

  const handleCancel = () => {
    setFormData({
      firstName: profileData.firstName || '',
      lastName: profileData.lastName || '',
      phone: profileData.phone || '',
      avatarUrl: profileData.avatarUrl || ''
    })
    setValidationErrors({})
    setIsEditing(false)
  }

  // Şifre değiştirme işlemleri
  const handlePasswordChange = (e) => {
    const { name, value } = e.target
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const validatePasswordForm = () => {
    const errors = {}
    
    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Mevcut şifre zorunludur'
    }
    
    if (!passwordData.newPassword) {
      errors.newPassword = 'Yeni şifre zorunludur'
    } else if (!ValidationPatterns.PASSWORD_STRONG.test(passwordData.newPassword)) {
      errors.newPassword = ValidationMessages.PASSWORD_STRONG
    }
    
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Şifre tekrarı zorunludur'
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Şifreler eşleşmiyor'
    }
    
    setPasswordErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    
    if (!validatePasswordForm()) {
      return
    }
    
    setIsChangingPassword(true)

    dispatch(
      ApiCallActions.Put({
        url: 'auth/change-password',
        data: {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        showAlertOnError: true,
        showLoading: true,
        onSuccess: () => {
          general.messageSuccess('Şifreniz başarıyla güncellendi')
          setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          })
          setPasswordErrors({})
        },
        callback: () => setIsChangingPassword(false)
      })
    )
  }

  const resetPasswordForm = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
    setPasswordErrors({})
  }

  const handleCopyInvitationLink = async () => {
    if (!invitationLink) return
    try {
      await navigator.clipboard.writeText(invitationLink)
      general.messageSuccess('Katılım linki kopyalandı')
    } catch (error) {
      console.error('Clipboard error', error)
      general.notificationError('Link kopyalanamadı, lütfen elle kopyalayın.')
    }
  }

  const getFullName = () => {
    return `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim() || 'Kullanıcı'
  }
  
  const getDefaultAvatar = () => {
    console.log("av",userInfo)
    if (userInfo.avatarUrl) {

      return userInfo.avatarUrl;
      
    }
    
    switch (userInfo.userType) {
      case UserTypeEnum.ADMIN:
        return '/assets/images/adminphoto.png';
      case UserTypeEnum.STUDENT:
        return '/assets/images/profile-photo.webp';
      case UserTypeEnum.AGENT:
        return '/assets/images/agentphoto.png';
      default:
        return '/assets/images/adminphoto.png';
    }
  };

  const getUserTypeLabel = () => {
    const types = {
      [UserTypeEnum.STUDENT]: 'Öğrenci',
      [UserTypeEnum.TEACHER]: 'Öğretmen',
      [UserTypeEnum.ADMIN]: 'Yönetici'
    }
    return types[profileData.userType] || profileData.userType || ''
  }

  const getStatusLabel = () => {
    const statuses = {
      active: 'Aktif',
      inactive: 'Pasif',
      pending: 'Beklemede'
    }
    return statuses[profileData.status] || profileData.status || ''
  }

  const getContractStatusLabel = () => {
    if (!contractStatus?.status) return '-'
    const statusMap = {
      active: 'Aktif Sözleşme',
      expired: 'Süresi Dolmuş',
      no_contract: 'Sözleşme Yok'
    }
    return statusMap[contractStatus.status] || contractStatus.status
  }

  const getContractStatusClass = () => {
    if (!contractStatus?.status) return 'badge-secondary'
    const statusClassMap = {
      active: 'badge-success',
      expired: 'badge-danger',
      no_contract: 'badge-warning'
    }
    return statusClassMap[contractStatus.status] || 'badge-secondary'
  }

  const getContractStatusTone = () => {
    if (!contractStatus?.status) return '#94a3b8'
    const toneMap = {
      active: '#22c55e',
      expired: '#ef4444',
      no_contract: '#f59e0b'
    }
    return toneMap[contractStatus.status] || '#94a3b8'
  }

  const formatDateValue = (value) => {
    if (!value) return '-'
    return general.formatDate(value)
  }

  if (loading) {
    return (
      <section className="content profile-page">
        <div className="container-fluid">
          <div className="row clearfix">
            <div className="col-12 text-center p-5">
              <p>Yükleniyor...</p>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="content profile-page">
      <div className="block-header">
        <div className="row">
          <div className="col-lg-7 col-md-6 col-sm-12 text-left">
            <h2>Profil
              <small>Profil Bilgileriniz</small>
            </h2>
          </div>
          <div className="col-lg-5 col-md-6 col-sm-12">
            {/* {!isEditing && (
              <button 
                className="btn btn-white btn-icon btn-round float-right m-l-10" 
                type="button"
                onClick={() => setIsEditing(true)}
              >
                <i className="zmdi zmdi-edit"></i>
              </button>
            )} */}
            <ul className="breadcrumb float-md-right">
              <li className="breadcrumb-item">
                <Link to="/dashboard">
                  <i className="zmdi zmdi-home"></i> Ana Sayfa
                </Link>
              </li>
              <li className="breadcrumb-item active">Profil</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="row clearfix">
          <div className="col-lg-4 col-md-12">
            <div className="card profile-header">
              <div className="body text-center">
                <div className="profile-image">
                  <img 
                     src={getDefaultAvatar()} 
                    alt="Profile"
                    onError={(e) => {
                      e.target.src = '/assets/images/profile-photo.webp'
                    }}
                  />
                </div>
                <div>
                  <h4 className="m-b-0"><strong>{getFullName()}</strong></h4>
                  <span className="job_post">{getUserTypeLabel()}</span>
                  <p>
                    {profileData.email && (
                      <>
                        <i className="zmdi zmdi-email m-r-5"></i>
                        {profileData.email}
                        {profileData.emailVerified && (
                          <i className="zmdi zmdi-check-circle text-success m-l-5" title="E-posta doğrulandı"></i>
                        )}
                        <br />
                      </>
                    )}
                    {profileData.phone && (
                      <>
                        <i className="zmdi zmdi-phone m-r-5"></i>
                        {profileData.phone}
                      </>
                    )}
                  </p>
                </div>
                <div>
                  <span className={`badge badge-${profileData.status === 'active' ? 'success' : 'warning'}`}>
                    {getStatusLabel()}
                  </span>
                </div>
              </div>
            </div>
            {invitationLink && (
              <div className="card">
                <div className="body">
                  <h5 className="mb-2">Öğrenci Katılım Linki</h5>
                  <p className="mb-3 text-muted">
                    Linki öğrencilerinizle paylaşın; kayıt olduklarında otomatik olarak acentanıza bağlanırlar.
                  </p>
                  <div className="d-flex align-items-center">
                    <input
                      type="text"
                      readOnly
                      className="form-control"
                      value={invitationLink}
                      onFocus={(e) => e.target.select()}
                      style={{ minWidth: 220 }}
                    />
                    <button
                      type="button"
                      className="btn btn-warning btn-round m-l-10"
                      onClick={handleCopyInvitationLink}
                      title="Linki kopyala"
                    >
                      <i className="zmdi zmdi-copy"></i>
                      </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="col-lg-8 col-md-12">
            <div className="card">
              <ul className="nav nav-tabs">
                <li className="nav-item">
                  <a 
                    className={`nav-link ${activeTab === 'about' ? 'active' : ''}`}
                    href="#about"
                    onClick={(e) => {
                      e.preventDefault()
                      setActiveTab('about')
                      setSearchParams({ tab: 'about' })
                    }}
                  >
                    Hakkında
                  </a>
                </li>
                {profileData.userType === 'agent' && (
                  <li className="nav-item">
                    <a
                      className={`nav-link ${activeTab === 'contract' ? 'active' : ''}`}
                      href="#contract"
                      onClick={(e) => {
                        e.preventDefault()
                        setActiveTab('contract')
                        setSearchParams({ tab: 'contract' })
                      }}
                    >
                      Sözleşme
                    </a>
                  </li>
                )}
                {profileData.userType === 'student' && (
                  <li className="nav-item">
                    <a 
                      className={`nav-link ${activeTab === 'student' ? 'active' : ''}`}
                      href="#student"
                      onClick={(e) => {
                        e.preventDefault()
                        setActiveTab('student')
                        setSearchParams({ tab: 'student' })
                      }}
                    >
                      Öğrenci Bilgileri
                    </a>
                  </li>
                )}
                <li className="nav-item">
                  <a 
                    className={`nav-link ${activeTab === 'account' ? 'active' : ''}`}
                    href="#account"
                    onClick={(e) => {
                      e.preventDefault()
                      setActiveTab('account')
                      setSearchParams({ tab: 'account' })
                    }}
                  >
                    Hesap
                  </a>
                </li>
                <li className="nav-item">
                  <a 
                    className={`nav-link ${activeTab === 'password' ? 'active' : ''}`}
                    href="#password"
                    onClick={(e) => {
                      e.preventDefault()
                      setActiveTab('password')
                      setSearchParams({ tab: 'password' })
                    }}
                  >
                    <i className="zmdi zmdi-lock m-r-5"></i>
                    Şifre Değiştir
                  </a>
                </li>
              </ul>

              <div className="tab-content">
                <div className={`tab-pane body ${activeTab === 'about' ? 'active' : ''}`} id="about">
                  <ul className="list-unstyled">
                    <li>
                      <p>
                        <i className="zmdi zmdi-account m-r-5"></i>
                        <strong>Ad Soyad:</strong> {getFullName()}
                      </p>
                    </li>
                    <li>
                      <p>
                        <i className="zmdi zmdi-email m-r-5"></i>
                        <strong>E-posta:</strong> {profileData.email || '-'}
                        {profileData.emailVerified && (
                          <span className="badge badge-success m-l-5">Doğrulandı</span>
                        )}
                      </p>
                    </li>
                    <li>
                      <p>
                        <i className="zmdi zmdi-phone m-r-5"></i>
                        <strong>Telefon:</strong> {profileData.phone || '-'}
                      </p>
                    </li>
                    <li>
                      <p>
                        <i className="zmdi zmdi-account-box m-r-5"></i>
                        <strong>Kullanıcı Tipi:</strong> {getUserTypeLabel()}
                      </p>
                    </li>
                    <li>
                      <p>
                        <i className="zmdi zmdi-info m-r-5"></i>
                        <strong>Durum:</strong> {getStatusLabel()}
                      </p>
                    </li>
                  </ul>
                </div>

                {profileData.userType === 'agent' && (
                  <div className={`tab-pane body ${activeTab === 'contract' ? 'active' : ''}`} id="contract">
                    {loadingContractStatus ? (
                      <div className="text-center p-4">
                        <p>Sözleşme bilgileri yükleniyor...</p>
                      </div>
                    ) : contractError ? (
                      <div className="alert alert-warning">{contractError}</div>
                    ) : contractStatus ? (
                      <div className="contract-profile-card">
                        <div className="contract-profile-header">
                          <div className="contract-profile-title">
                            <i className="zmdi zmdi-file-text"></i>
                            <div>
                              <h5 className="m-b-0">Sözleşme Detayları</h5>
                              <small className="text-muted">Güncel sözleşme bilgileriniz</small>
                            </div>
                          </div>
                          <span className={`badge ${getContractStatusClass()}`}>
                            {getContractStatusLabel()}
                          </span>
                        </div>

                        <div className="contract-status-row">
                          <div className="contract-status-pill" style={{ borderColor: `${getContractStatusTone()}33` }}>
                            <span className="pill-icon" style={{ color: getContractStatusTone() }}>
                              <i className="zmdi zmdi-check-circle"></i>
                            </span>
                            <div>
                              <small>Durum</small>
                              <strong>{getContractStatusLabel()}</strong>
                            </div>
                          </div>
                          <div
                            className="contract-status-pill"
                            style={{ borderColor: contractStatus.contractExpiringSoon ? '#f59e0b33' : '#e2e8f0' }}
                          >
                            <span
                              className="pill-icon"
                              style={{ color: contractStatus.contractExpiringSoon ? '#f59e0b' : '#94a3b8' }}
                            >
                              <i className="zmdi zmdi-time-countdown"></i>
                            </span>
                            <div>
                              <small>Uyarı</small>
                              <strong>{contractStatus.contractExpiringSoon ? '30 gün içinde bitiyor' : 'Yok'}</strong>
                            </div>
                          </div>
                        </div>

                        <div className="contract-info-grid">
                          <div className="contract-info-item">
                            <span className="info-label">Sözleşme Başlangıç</span>
                            <span className="info-value">{formatDateValue(contractStatus.contractStartDate)}</span>
                          </div>
                          <div className="contract-info-item">
                            <span className="info-label">Sözleşme Bitiş</span>
                            <span className="info-value">{formatDateValue(contractStatus.contractEndDate)}</span>
                          </div>
                          <div className="contract-info-item">
                            <span className="info-label">Lisans Numarası</span>
                            <span className="info-value">{contractStatus.licenseNumber || '-'}</span>
                          </div>
                          <div className="contract-info-item">
                            <span className="info-label">Lisans Bitiş</span>
                            <span className="info-value">{formatDateValue(contractStatus.licenseExpiryDate)}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center p-4">
                        <p>Sözleşme bilgisi bulunamadı.</p>
                      </div>
                    )}
                  </div>
                )}

                {profileData.userType === 'student' && (
                  <div className={`tab-pane body ${activeTab === 'student' ? 'active' : ''}`} id="student">
                  <StudentForm mode="self" />
                  </div>
                )}

                <div className={`tab-pane body ${activeTab === 'account' ? 'active' : ''}`} id="account">
                  <form onSubmit={handleSubmit}>
                    <div className="row clearfix">
                      <div className="col-lg-6 col-md-12">
                        <div className="form-group">
                          <label>Ad</label>
                          <Input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            placeholder="Ad"
                            disabled={!isEditing}
                            validationRegex={ValidationPatterns.NAME}
                            errorMessage={ValidationMessages.NAME}
                            onValidationChange={handleValidationChange}
                            validateOnBlur={isEditing}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-12">
                        <div className="form-group">
                          <label>Soyad</label>
                          <Input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            placeholder="Soyad"
                            disabled={!isEditing}
                            validationRegex={ValidationPatterns.NAME}
                            errorMessage={ValidationMessages.NAME}
                            onValidationChange={handleValidationChange}
                            validateOnBlur={isEditing}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-12">
                        <div className="form-group">
                          <label>Telefon</label>
                          <Input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="Telefon (örn: +905551234567)"
                            disabled={!isEditing}
                            required={false}
                            validationRegex={ValidationPatterns.PHONE_GENERAL}
                            errorMessage={ValidationMessages.PHONE_GENERAL}
                            onValidationChange={handleValidationChange}
                            validateOnBlur={isEditing}
                          />
                        </div>
                      </div>
                      <div className="col-lg-12 col-md-12">
                        <div className="form-group">
                          <FileUpload
                            uploadUrl="files/profile/avatar"
                            accept=".jpg,.jpeg,.png,.webp"
                            maxSize={5}
                            label="Profil Fotoğrafı"
                            showPreview={true}
                            fileType="image"
                            disabled={!isEditing}
                            currentFileUrl={profileData.avatarUrl}
                            onUploadSuccess={(uploadedData) => {
                              if (uploadedData && uploadedData.url) {
                                // Profil bilgilerini yeniden çek - fetchProfile userInfo'yu da güncelleyecek
                                fetchProfile()
                              }
                            }}
                            onRemove={() => {
                              // Avatar URL'ini temizle
                              setProfileData(prev => ({
                                ...prev,
                                avatarUrl: ''
                              }))
                              setFormData(prev => ({
                                ...prev,
                                avatarUrl: ''
                              }))
                            
                            }}
                            onUploadError={(error) => {
                              console.error('Avatar upload error:', error)
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-md-12">
                        {isEditing ? (
                          <div>
                            <button 
                              type="submit" 
                              className="btn btn-primary btn-round"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-default btn-round m-l-10"
                              onClick={handleCancel}
                              disabled={isSubmitting}
                            >
                              İptal
                            </button>
                          </div>
                        ) : (
                          <button 
                            type="button" 
                            className="btn btn-info btn-round"
                            onClick={() => setIsEditing(true)}
                          >
                            Düzenle
                          </button>
                        )}
                      </div>
                    </div>
                  </form>
                </div>

                {/* Şifre Değiştirme Sekmesi */}
                <div className={`tab-pane body ${activeTab === 'password' ? 'active' : ''}`} id="password">
                  <div className="alert alert-info">
                    <i className="zmdi zmdi-info-outline m-r-10"></i>
                    Güvenliğiniz için güçlü bir şifre kullanın: En az 8 karakter, büyük harf, küçük harf, rakam ve özel karakter içermelidir.
                  </div>
                  
                  <form onSubmit={handlePasswordSubmit}>
                    <div className="row clearfix">
                      <div className="col-lg-6 col-md-12">
                        <div className="form-group">
                          <label>Mevcut Şifre <span className="text-danger">*</span></label>
                          <Input
                            type="password"
                            name="currentPassword"
                            value={passwordData.currentPassword}
                            onChange={handlePasswordChange}
                            placeholder="Mevcut şifrenizi girin"
                            showPasswordToggle
                          />
                          {passwordErrors.currentPassword && (
                            <small className="text-danger">{passwordErrors.currentPassword}</small>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row clearfix">
                      <div className="col-lg-6 col-md-12">
                        <div className="form-group">
                          <label>Yeni Şifre <span className="text-danger">*</span></label>
                          <Input
                            type="password"
                            name="newPassword"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            placeholder="Yeni şifrenizi girin"
                            showPasswordToggle
                          />
                          {passwordErrors.newPassword && (
                            <small className="text-danger">{passwordErrors.newPassword}</small>
                          )}
                        </div>
                      </div>

                      <div className="col-lg-6 col-md-12">
                        <div className="form-group">
                          <label>Yeni Şifre Tekrar <span className="text-danger">*</span></label>
                          <Input
                            type="password"
                            name="confirmPassword"
                            value={passwordData.confirmPassword}
                            onChange={handlePasswordChange}
                            placeholder="Yeni şifrenizi tekrar girin"
                            showPasswordToggle
                          />
                          {passwordErrors.confirmPassword && (
                            <small className="text-danger">{passwordErrors.confirmPassword}</small>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Şifre Güç Göstergesi */}
                    {passwordData.newPassword && (
                      <div className="row clearfix m-b-20">
                        <div className="col-lg-6 col-md-12">
                          <label className="m-b-5">Şifre Gücü</label>
                          <div className="progress" style={{ height: '8px' }}>
                            <div 
                              className={`progress-bar ${
                                passwordData.newPassword.length < 6 ? 'bg-danger' :
                                passwordData.newPassword.length < 8 ? 'bg-warning' :
                                ValidationPatterns.PASSWORD_STRONG.test(passwordData.newPassword) ? 'bg-success' : 'bg-info'
                              }`}
                              style={{ 
                                width: `${
                                  passwordData.newPassword.length < 6 ? '25' :
                                  passwordData.newPassword.length < 8 ? '50' :
                                  ValidationPatterns.PASSWORD_STRONG.test(passwordData.newPassword) ? '100' : '75'
                                }%` 
                              }}
                            ></div>
                          </div>
                          <small className={`${
                            passwordData.newPassword.length < 6 ? 'text-danger' :
                            passwordData.newPassword.length < 8 ? 'text-warning' :
                            ValidationPatterns.PASSWORD_STRONG.test(passwordData.newPassword) ? 'text-success' : 'text-info'
                          }`}>
                            {passwordData.newPassword.length < 6 ? 'Çok Zayıf' :
                             passwordData.newPassword.length < 8 ? 'Zayıf' :
                             ValidationPatterns.PASSWORD_STRONG.test(passwordData.newPassword) ? 'Güçlü' : 'Orta'}
                          </small>
                        </div>
                      </div>
                    )}

                    <div className="row clearfix">
                      <div className="col-md-12">
                        <button 
                          type="submit" 
                          className="btn btn-primary btn-round"
                          disabled={isChangingPassword}
                        >
                          {isChangingPassword ? (
                            <>
                              <i className="zmdi zmdi-spinner zmdi-hc-spin m-r-5"></i>
                              Güncelleniyor...
                            </>
                          ) : (
                            <>
                              <i className="zmdi zmdi-lock m-r-5"></i>
                              Şifreyi Güncelle
                            </>
                          )}
                        </button>
                        <button 
                          type="button" 
                          className="btn btn-default btn-round m-l-10"
                          onClick={resetPasswordForm}
                          disabled={isChangingPassword}
                        >
                          Temizle
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Profile

